'use client';

import * as React from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { LANGUAGES, LanguageCode } from '@/data/languages';
import { cn } from "@/lib/utils";

interface TranslatableInputProps extends Omit<React.ComponentProps<'input'>, 'value' | 'onChange'> {
    value?: Record<string, string>;
    onChange: (value: Record<string, string>) => void;
    selectedLanguage: LanguageCode;
    onLanguageChange: (language: LanguageCode) => void;
    availableLanguages?: LanguageCode[];
    label?: string;
    mode?: 'input' | 'textarea';
    required?: boolean;
}

/**
 * TranslatableInput - Input field with language selector
 * Allows users to input translations for multiple languages
 */
export const TranslatableInput = React.memo(
    React.forwardRef<HTMLInputElement, TranslatableInputProps>(
        (
            {
                className,
                value = {},
                onChange,
                selectedLanguage,
                onLanguageChange,
                availableLanguages,
                label,
                mode = 'input',
                required = false,
                ...props
            },
            ref,
        ) => {
            // Local state for immediate updates
            const [localValue, setLocalValue] = React.useState(value[selectedLanguage] ?? '');

            // Update local value when selected language or value changes
            React.useEffect(() => {
                setLocalValue(value[selectedLanguage] ?? '');
            }, [selectedLanguage, value]);

            const handleInputChange = React.useCallback(
                (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                    const newValue = e.target.value;
                    setLocalValue(newValue);

                    // Use requestAnimationFrame to batch updates
                    requestAnimationFrame(() => {
                        onChange({
                            ...value,
                            [selectedLanguage]: newValue,
                        });
                    });
                },
                [selectedLanguage, onChange, value],
            );

            const languageOptions = React.useMemo(() => {
                const codes = availableLanguages || (Object.keys(LANGUAGES) as LanguageCode[]);
                return codes.map((code: LanguageCode) => ({
                    code,
                    ...LANGUAGES[code],
                }));
            }, [availableLanguages]);

            const selectedLangName = LANGUAGES[selectedLanguage as keyof typeof LANGUAGES]?.name || selectedLanguage;

            return (
                <div className="space-y-2">
                    {label && (
                        <Label>
                            {label}
                            {required && <span className="text-red-500 ml-1">*</span>}
                            <span className="text-muted-foreground ml-2 text-xs font-normal">
                                ({selectedLangName})
                            </span>
                        </Label>
                    )}
                    <div className={cn('flex gap-2', className)}>
                        <Select
                            value={selectedLanguage}
                            onValueChange={(value) => onLanguageChange(value as LanguageCode)}
                        >
                            <SelectTrigger className="w-[120px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="max-h-[300px]">
                                {languageOptions.map((lang: { code: string; name: string }) => (
                                    <SelectItem key={lang.code} value={lang.code}>
                                        {lang.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {mode === 'input' ? (
                            <Input
                                ref={ref as React.Ref<HTMLInputElement>}
                                value={localValue}
                                onChange={handleInputChange}
                                className="flex-1"
                                placeholder={`Enter ${selectedLangName} translation...`}
                                {...props}
                            />
                        ) : (
                            <Textarea
                                value={localValue}
                                onChange={handleInputChange}
                                className="flex-1"
                                placeholder={`Enter ${selectedLangName} translation...`}
                                {...(props as React.ComponentProps<'textarea'>)}
                            />
                        )}
                    </div>
                </div>
            );
        },
    ),
);

TranslatableInput.displayName = 'TranslatableInput';

/**
 * Helper hook to manage translation state
 */
export const useTranslationState = (initialValue: Record<string, string> = {}) => {
    const [translations, setTranslations] = React.useState(initialValue);
    const [selectedLanguage, setSelectedLanguage] = React.useState<LanguageCode>('en');

    return {
        translations,
        setTranslations,
        selectedLanguage,
        setSelectedLanguage,
    };
};
